import moment from "moment";
import {
  type GetPerpetualEndDate,
  type HandleCalendarView,
  type CalculateNoOfInstallments,
  type Day,
  type GetMinimumEndDate,
  type GetMinimumStartDate,
  type ShouldDisableEndDate,
  type ShouldDisableStartDate,
  type MultiplierMap,
  type GetMinimumStartDateWeekly,
} from "../store/types/transactions";

import { handleError } from "./cart";

export const multipliers: MultiplierMap = {
  DAILY: 1.0,
  WEEKLY: 7.0,
  FORTNIGHTLY: 14.0,
  MONTHLY: 1.0,
  QUARTERLY: 3.0,
  HALFYEARLY: 6.0,
  YEARLY: 1.0,
};

enum Frequency {
  D = "DAILY",
  W = "WEEKLY",
  F = "FORTNIGHTLY",
  M = "MONTHLY",
  Q = "QUARTERLY",
  H = "HALFYEARLY",
  Y = "YEARLY",
}

const daysList: Array<Day> = [
  "Sunday",
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
];

export const capitalize = function (str: string) {
  return str;
};

export const getPerpetualSipEndDate: GetPerpetualEndDate = function (args) {
  const { frequency, modeOfRegistration, startDate } = args;

  const today = new Date(new Date().setHours(0, 0, 0, 0));
  const sipStartDate = new Date(startDate);

  const currentYear = today.getFullYear();
  const currentMonth = today.getMonth();
  const currentDate = today.getDate();

  if (modeOfRegistration === "ISIP") {
    return new Date(new Date(startDate).setFullYear(2099));
  }

  if ([Frequency.D, Frequency.W, Frequency.F].includes(frequency)) {
    const endYear = new Date(today).setFullYear(currentYear + 40);
    const endDate = new Date(new Date(endYear).setDate(currentDate - 1));
    return endDate;
  }

  const endYear = new Date(today).setFullYear(currentYear + 40);
  const endMonth = new Date(new Date(endYear).setMonth(currentMonth - 1));

  const endDate = new Date(new Date(endMonth).setDate(sipStartDate.getDate()));
  return endDate;
};

export const handleCalendarView: HandleCalendarView = function (freq) {
  if ([Frequency.D, Frequency.W, Frequency.F].includes(freq)) {
    return ["date", "month", "year"];
  }
  if (freq === Frequency.Y) {
    return ["year"];
  }
  return ["month", "year"];
};

export const calculateNoOfInstallments: CalculateNoOfInstallments = function (
  args
) {
  try {
    const { endDate, frequency, startDate } = args;

    const [momentStartDate, momentEndDate] = [startDate, endDate]
      .map((d) => new Date(new Date(d).setHours(0, 0, 0, 0)))
      .map((d) => moment(d));

    if ([Frequency.D, Frequency.W, Frequency.F].includes(frequency)) {
      const dayMultiplier = multipliers[frequency];
      const noOfDays = momentEndDate.diff(momentStartDate, "days");
      const installments = Math.trunc(noOfDays / dayMultiplier);
      return installments + 1;
    }

    if ([Frequency.M, Frequency.Q, Frequency.H].includes(frequency)) {
      const monthMultiplier = multipliers[frequency];
      const noOfMonths = momentEndDate.diff(momentStartDate, "months");
      const installments = Math.trunc(noOfMonths / monthMultiplier);
      return installments + 1;
    }
    return moment(endDate).diff(moment(startDate), "years") + 1;
  } catch (error) {
    handleError(error);
  }
};

export const getMinimumStartDateWeekly: GetMinimumStartDateWeekly = function (
  args
) {
  const { sipDay, sipTat } = args;

  const today = new Date();
  const sipTatDate = new Date(
    new Date(today).setDate(today.getDate() + sipTat)
  );

  const capitalizedWeekDays = daysList.map((day) => day.toUpperCase());
  const selectedSipDay = sipDay
    .trim()
    .split(" ")
    .map((d) => d.toUpperCase());

  const isValidDay = capitalizedWeekDays.some((day) =>
    selectedSipDay.includes(day)
  );

  if (!isValidDay) {
    return sipTatDate;
  }

  const selectedWeekDay = capitalizedWeekDays.findIndex((day) =>
    selectedSipDay.includes(day)
  );
  const difference = Math.abs(sipTatDate.getDay() - selectedWeekDay);

  if (difference === 0) {
    return moment(sipTatDate).add(1, "week").toDate();
  }
  return moment(sipTatDate).add(difference, "days").toDate();
};

export const getDaysToAdd = function (day: Date): 0 | 1 | 2 {
  const currentDay = day.getDay();

  if (currentDay === 6) return 2;
  if (currentDay === 0) return 1;
  return 0;
};

export const getMinimumStartDate: GetMinimumStartDate = function (args) {
  const { sipTat } = args;
  const startDate = moment()
    .add(sipTat + 1, "days")
    .toDate();
  const updatedDate = moment(startDate)
    .add(getDaysToAdd(startDate), "days")
    .toDate();

  return updatedDate;
};

export const getMinimumEndDate: GetMinimumEndDate = function (args) {
  const { frequency, minimumInstallments, startDate } = args;
  const multiplier = multipliers[frequency];
  const decrementedInstallments = minimumInstallments - 1;

  if (frequency === Frequency.D) {
    const minEndDate = moment(startDate)
      .add(decrementedInstallments * multiplier, "days")
      .toDate();

    return moment(minEndDate)
      .add(calculateWeekends(startDate, minEndDate), "days")
      .toDate();
  }

  if ([Frequency.W, Frequency.F].includes(frequency)) {
    const minEndDate = moment(startDate)
      .add(decrementedInstallments * multiplier, "days")
      .toDate();

    return minEndDate;
  }

  if ([Frequency.M, Frequency.Q, Frequency.H].includes(frequency)) {
    const minEndDate = new Date(
      new Date(startDate).setMonth(
        startDate.getMonth() + decrementedInstallments * multiplier
      )
    );
    return minEndDate;
  }

  return new Date(
    new Date(startDate).setFullYear(
      startDate.getFullYear() + minimumInstallments
    )
  );
};

export const shouldDisableEndDate: ShouldDisableEndDate = function (args) {
  return function (day) {
    const currentDay = day.getDay();

    if (Frequency.D === args.frequency) {
      return currentDay === 6 || currentDay === 0;
    }

    if (Frequency.W === args.frequency) {
      return args.startDate.getDay() !== currentDay;
    }

    const isOverMonthly = [
      Frequency.M,
      Frequency.Q,
      Frequency.H,
      Frequency.Y,
    ].includes(args.frequency);

    if (isOverMonthly) {
      return day.getDate() !== args.startDate.getDate();
    }

    return false;
  };
};

export const shouldDisableStartDate: ShouldDisableStartDate = function (args) {
  return function (day) {
    const currentDay = day.getDay();
    const { sipDay } = args;

    if (currentDay === 6 || currentDay === 0) {
      return true;
    }

    const isNumberArray = args.sipDays.every((d) => !isNaN(Number(d.value)));

    const isNumber = !isNaN(Number(sipDay));

    if (isNumberArray || isNumber) {
      return !args.sipDays.some((d) => Number(d.value) === day.getDate());
    }

    if (daysList.slice(1)) {
    }
    return day.getDate() > 28;
  };
};

export function calculateWeekends(
  startDate: Date,
  endDate: Date,
  accumulatedWeekends = 0
): number {
  const weekends = getWeekendCount(startDate, endDate);

  if (weekends === 0) {
    return accumulatedWeekends;
  }

  const refreshedEndDate = moment(endDate).add(weekends, "days").toDate();

  return calculateWeekends(
    endDate,
    refreshedEndDate,
    weekends + accumulatedWeekends
  );
}

export function getWeekendCount(startDate: Date, endDate: Date) {
  const diff = moment(endDate).diff(moment(startDate), "days");

  const weekends = Array.from({ length: diff }, (_, i) =>
    moment(startDate).add(i, "days")
  ).filter((d) => d.day() === 0 || d.day() === 6);

  return weekends.length;
}
